import React, { useState, useMemo, useRef, useCallback, useEffect } from "react";
import toast from "react-hot-toast";
import {
  MdAutoAwesome,
  MdRefresh,
  MdClose,
  MdCheck,
  MdErrorOutline,
  MdDeleteOutline,
  MdCalendarToday,
  MdChevronLeft,
  MdChevronRight,
  MdKeyboardArrowDown,
  MdHourglassEmpty,
  MdAdd,
} from "react-icons/md";
import SearchBar from "../SearchBar/SearchBar";
import InternalPagination from "../InternalPagination/InternalPagination";
import ZoomButton from "../buttons/ZoomButton";
import "./AIGenerationsView.css";

const formatProperDate = (timestamp) => {
  if (!timestamp) return "";
  const d = new Date(timestamp);
  if (isNaN(d.getTime())) return "";

  const now = new Date();
  const isCurrentYear = d.getFullYear() === now.getFullYear();

  const dateStr = d.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    ...(isCurrentYear ? {} : { year: "numeric" }),
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });

  // Replace space before AM/PM with non-breaking space so PM can never wrap alone
  return dateStr.replace(/\s+(AM|PM)$/i, "\u00A0$1");
};

const ExpandablePrompt = ({ name = "", prompt = "", className = "" }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const fullName = name || prompt || "AI Generated Item";

  return (
    <div
      className={`min-w-0 cursor-pointer group/prompt select-none ${className}`}
      onClick={(e) => {
        e.stopPropagation();
        setIsExpanded((prev) => !prev);
      }}
      title={isExpanded ? "Click to collapse" : "Click to view full text"}
    >
      <h4
        className={`text-xs sm:text-[13px] font-semibold text-slate-800 m-0 group-hover/prompt:text-primary transition-colors leading-snug ${
          isExpanded ? "whitespace-normal break-words" : "truncate"
        }`}
      >
        {fullName}
      </h4>
    </div>
  );
};

const AIGenerationsView = ({
  header = null,
  jobs = [],
  history = [],
  historyStats = null,
  pagination = null,
  filters = [],
  isFetching = false,
  onFetchHistory = null,
  onRetry,
  onCancel,
  onDismiss,
  onSaveToLibrary,
  onZoom,
  onOpenDrawer,
  onDeleteHistoryItem,
  modalTarget = "#bgListingZoomModal",
  entityType = "background",
  entityLabel = "Background",
  pluralLabel = "Backdrops",
  cardAspect = "aspect-[4/3]",
  createButtonText = "Generate Background",
}) => {
  const [statusFilter, setStatusFilter] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");
  const [localPage, setLocalPage] = useState(1);
  const [localLimit, setLocalLimit] = useState(10);
  const [isQueueCollapsed, setIsQueueCollapsed] = useState(false);
  const prevJobsCountRef = useRef(jobs.length);
  const isFirstMountRef = useRef(true);

  // Auto-expand queue rail if new generations are queued
  useEffect(() => {
    if (jobs.length > prevJobsCountRef.current) {
      setIsQueueCollapsed(false);
    }
    prevJobsCountRef.current = jobs.length;
  }, [jobs.length]);

  // Queue Rail Horizontal Scroll State & Logic
  const queueRailRef = useRef(null);
  const [queueScrollState, setQueueScrollState] = useState({
    canScrollLeft: false,
    canScrollRight: false,
  });

  const updateQueueScrollState = useCallback(() => {
    if (queueRailRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = queueRailRef.current;
      setQueueScrollState({
        canScrollLeft: scrollLeft > 2,
        canScrollRight: scrollLeft + clientWidth < scrollWidth - 2,
      });
    }
  }, []);

  useEffect(() => {
    const el = queueRailRef.current;
    if (!el) return;

    updateQueueScrollState();
    el.addEventListener("scroll", updateQueueScrollState, { passive: true });
    window.addEventListener("resize", updateQueueScrollState);

    const timer = setTimeout(updateQueueScrollState, 150);

    return () => {
      clearTimeout(timer);
      el.removeEventListener("scroll", updateQueueScrollState);
      window.removeEventListener("resize", updateQueueScrollState);
    };
  }, [jobs, isQueueCollapsed, updateQueueScrollState]);

  useEffect(() => {
    if (!isQueueCollapsed) {
      const timer1 = setTimeout(updateQueueScrollState, 50);
      const timer2 = setTimeout(updateQueueScrollState, 330);
      return () => {
        clearTimeout(timer1);
        clearTimeout(timer2);
      };
    }
  }, [isQueueCollapsed, updateQueueScrollState]);

  const scrollQueueRail = (direction) => {
    if (queueRailRef.current) {
      const scrollAmount = queueRailRef.current.clientWidth * 0.75;
      queueRailRef.current.scrollBy({
        left: direction * scrollAmount,
        behavior: "smooth",
      });
    }
  };

  // Debounced server search / filter sync
  useEffect(() => {
    if (isFirstMountRef.current) {
      isFirstMountRef.current = false;
      return;
    }

    const timer = setTimeout(() => {
      if (onFetchHistory) {
        setLocalPage(1);
        onFetchHistory({
          page: 1,
          limit: pagination?.limit || localLimit,
          status: statusFilter !== "all" ? statusFilter : undefined,
          search: searchTerm.trim() || undefined,
        });
      }
    }, 320);

    return () => clearTimeout(timer);
  }, [searchTerm, statusFilter]); // eslint-disable-line react-hooks/exhaustive-deps

  // Handle Page Change
  const handlePageChange = (newPage) => {
    setLocalPage(newPage);
    if (onFetchHistory) {
      onFetchHistory({
        page: newPage,
        limit: pagination?.limit || localLimit,
        status: statusFilter !== "all" ? statusFilter : undefined,
        search: searchTerm.trim() || undefined,
      });
    }
  };

  // Handle Limit Change
  const handleLimitChange = (newLimit) => {
    setLocalLimit(newLimit);
    setLocalPage(1);
    if (onFetchHistory) {
      onFetchHistory({
        page: 1,
        limit: newLimit,
        status: statusFilter !== "all" ? statusFilter : undefined,
        search: searchTerm.trim() || undefined,
      });
    }
  };

  // Filter items in history
  const displayedHistory = useMemo(() => {
    return history.filter((item) => {
      // If onFetchHistory was NOT provided (standalone fallback mode), apply search and status locally
      if (!onFetchHistory) {
        if (statusFilter !== "all" && item.status !== statusFilter) return false;

        if (searchTerm.trim()) {
          const query = searchTerm.toLowerCase();
          const promptMatch = item.prompt?.toLowerCase().includes(query);
          const nameMatch = item.name?.toLowerCase().includes(query);
          return Boolean(promptMatch || nameMatch);
        }
      }
      return true;
    });
  }, [history, onFetchHistory, statusFilter, searchTerm]);

  // Calculate pagination coordinates
  const activePage = pagination ? pagination.page : localPage;
  const activeLimit = pagination ? pagination.limit : localLimit;
  const activeTotal = pagination?.total ?? displayedHistory.length;
  const activeTotalPages =
    pagination?.totalPages ?? Math.max(1, Math.ceil(activeTotal / activeLimit));

  // If onFetchHistory is used, history is already paginated by server.
  // Otherwise, slice displayedHistory for client-side pagination fallback.
  const paginatedHistory = useMemo(() => {
    if (onFetchHistory) {
      return displayedHistory;
    }
    const start = (activePage - 1) * activeLimit;
    return displayedHistory.slice(start, start + activeLimit);
  }, [displayedHistory, onFetchHistory, activePage, activeLimit]);

  // Dynamic filter groups directly from API
  const activeFilters = useMemo(() => {
    return Array.isArray(filters) ? filters : [];
  }, [filters]);

  // Compute stat counts for pills
  const stats = historyStats || {
    total: history.length,
    queued: jobs.filter((j) => j.status === "queued").length,
    generating: jobs.filter((j) => j.status === "generating").length,
    completed: history.filter((h) => h.status === "completed").length,
    failed: history.filter((h) => h.status === "failed").length,
  };

  return (
    <div className="ai-gen-view-container flex-1 flex flex-col min-h-0 overflow-hidden">
      {/* Scrollable Content Body */}
      <div className="flex-1 overflow-y-auto min-h-0">
        {/* Page Header (Scrolls away, not sticky) */}
        {header}

        {/* Primary Controls Bar (Sticky at top when scrolling) */}
        <div className="product-controls-wrapper sticky top-0 z-30 px-4 sm:px-6 py-2 bg-[#F1F4F9]/95 backdrop-blur-sm">
          <div className="app-control-bar product-controls-shell flex flex-col sm:flex-row sm:items-center justify-between gap-2.5 sm:gap-4 p-2.5 sm:p-3">
            {/* Status Filter Pills dynamically rendered from API filters */}
            <div className="flex flex-wrap items-center gap-2 max-w-full shrink-0">
              {activeFilters.map((filterGroup) => (
                <div
                  key={filterGroup.key || filterGroup.label}
                  className="gender-tabs flex items-center gap-0.5 bg-slate-100/80 p-0.5 rounded-md overflow-x-auto no-scrollbar max-w-full shrink-0"
                >
                  {filterGroup.options.map((opt) => {
                    const isSelected =
                      statusFilter === opt.value || (!statusFilter && opt.value === "all");
                    const count =
                      opt.value === "all"
                        ? stats.total
                        : stats[opt.value] !== undefined
                        ? stats[opt.value]
                        : null;

                    return (
                      <button
                        key={opt.value}
                        type="button"
                        onClick={() => setStatusFilter(opt.value)}
                        className={`px-2.5 sm:px-3 py-1 rounded font-semibold text-[11px] sm:text-xs transition-all duration-200 whitespace-nowrap flex items-center gap-1.5 cursor-pointer ${
                          isSelected
                            ? opt.value === "failed"
                              ? "bg-red-600 text-white shadow-sm font-bold border-none"
                              : "bg-primary text-white shadow-sm font-bold border-none"
                            : "bg-white text-slate-500 border border-slate-200 hover:text-primary hover:border-slate-300 hover:shadow-sm"
                        } focus:outline-none`}
                      >
                        <span>{opt.label}</span>
                        {count !== null && (
                          <span
                            className={`text-[10px] px-1 py-0.2 rounded font-bold transition-colors ${
                              isSelected ? "bg-white/20 text-white" : "bg-slate-100 text-slate-600"
                            }`}
                          >
                            {count}
                          </span>
                        )}
                        {opt.value === "failed" && stats.failed > 0 && !isSelected && (
                          <span className="w-1.5 h-1.5 rounded-full bg-red-500 animate-pulse" />
                        )}
                      </button>
                    );
                  })}
                </div>
              ))}
            </div>

            {/* Search Input & Mobile Generate Button */}
            <div className="product-search-actions flex items-center gap-1.5 sm:gap-2 sm:justify-end relative min-w-0 w-full sm:w-auto">
              <SearchBar
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder={`Search AI ${entityType === "model" ? "models" : "generations"}...`}
                className="flex-1 min-w-0 w-full sm:w-64"
              />

              {!header && onOpenDrawer && (
                <button
                  type="button"
                  onClick={onOpenDrawer}
                  className="sm:hidden app-button-primary flex items-center justify-center gap-1 px-2.5 h-8 rounded-md text-xs font-semibold shadow-sm focus:outline-none cursor-pointer whitespace-nowrap shrink-0"
                  title={createButtonText}
                >
                  <MdAutoAwesome size={15} />
                  <span>Generate</span>
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Content Body */}
        <div className="px-4 sm:px-6 pb-6 pt-2">
          {/* SECTION 1: ACTIVE LIVE QUEUE (Horizontal Rail for 1-row compact footprint) */}
          {jobs.length > 0 && (
            <div className="bg-slate-50/70 border border-slate-200/90 rounded-xl p-3 sm:p-3.5 shadow-2xs mb-5 transition-[border-color,box-shadow] duration-200">
              <div className="flex items-center justify-between gap-2 select-none">
                <div
                  className="flex items-center gap-2 min-w-0 cursor-pointer group"
                  onClick={() => setIsQueueCollapsed((prev) => !prev)}
                  title={isQueueCollapsed ? "Click to expand queue" : "Click to collapse queue"}
                >
                  <span className="w-2 h-2 rounded-full bg-primary animate-pulse shrink-0" />
                  <h3 className="text-xs sm:text-sm font-bold text-slate-900 m-0 truncate group-hover:text-primary transition-colors">
                    Active Generation Queue
                  </h3>
                  <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-white text-primary border border-slate-200 shrink-0">
                    {jobs.length} {jobs.length === 1 ? "item" : "items"}
                  </span>
                  {jobs.some((j) => j.status === "failed") && (
                    <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-red-50 text-red-700 border border-red-200 shrink-0">
                      {jobs.filter((j) => j.status === "failed").length} failed
                    </span>
                  )}
                </div>

                {/* Navigation arrows & collapse button */}
                <div className="flex items-center gap-1.5 shrink-0">
                  <div
                    className={`flex items-center gap-1.5 transition-all duration-300 ease-in-out overflow-hidden ${
                      isQueueCollapsed
                        ? "max-w-0 opacity-0 pointer-events-none -mr-1"
                        : "max-w-[120px] opacity-100"
                    }`}
                  >
                    <button
                      type="button"
                      onClick={() => scrollQueueRail(-1)}
                      disabled={!queueScrollState.canScrollLeft}
                      className={`w-7 h-7 rounded-full border flex items-center justify-center transition-all shadow-2xs focus:outline-none shrink-0 ${
                        queueScrollState.canScrollLeft
                          ? "text-slate-700 border-slate-300 hover:text-slate-900 hover:border-slate-400 active:scale-95 cursor-pointer bg-white"
                          : "text-slate-300 bg-slate-50 border-slate-200/60 cursor-not-allowed"
                      }`}
                      aria-label="Scroll queue left"
                      title="Scroll queue left"
                    >
                      <MdChevronLeft size={16} />
                    </button>
                    <button
                      type="button"
                      onClick={() => scrollQueueRail(1)}
                      disabled={!queueScrollState.canScrollRight}
                      className={`w-7 h-7 rounded-full border flex items-center justify-center transition-all shadow-2xs focus:outline-none shrink-0 ${
                        queueScrollState.canScrollRight
                          ? "text-slate-700 border-slate-300 hover:text-slate-900 hover:border-slate-400 active:scale-95 cursor-pointer bg-white"
                          : "text-slate-300 bg-slate-50 border-slate-200/60 cursor-not-allowed"
                      }`}
                      aria-label="Scroll queue right"
                      title="Scroll queue right"
                    >
                      <MdChevronRight size={16} />
                    </button>
                    <div className="h-4 w-px bg-slate-200 mx-0.5 shrink-0" />
                  </div>

                  <button
                    type="button"
                    onClick={() => setIsQueueCollapsed((prev) => !prev)}
                    className="w-7 h-7 rounded-full border border-slate-200 bg-white hover:bg-slate-100 hover:border-slate-300 text-slate-600 hover:text-slate-900 flex items-center justify-center transition-all shadow-2xs cursor-pointer focus:outline-none"
                    aria-label={isQueueCollapsed ? "Expand queue" : "Collapse queue"}
                    title={isQueueCollapsed ? "Expand queue" : "Collapse queue"}
                  >
                    <MdKeyboardArrowDown
                      size={18}
                      className={`transition-transform duration-300 ease-in-out ${
                        isQueueCollapsed ? "rotate-0" : "rotate-180"
                      }`}
                    />
                  </button>
                </div>
              </div>

              {/* Horizontal Rail Collapsible Container */}
              <div
                className={`ai-queue-collapsible-wrapper ${
                  isQueueCollapsed ? "collapsed" : ""
                }`}
              >
                <div className="ai-queue-collapsible-content">
                  <div className="pt-2.5 mt-2.5 border-t border-slate-200/80">
                    <div
                      ref={queueRailRef}
                      className="flex items-start gap-3 overflow-x-auto rail-container pt-0.5 pb-1 scroll-smooth"
                    >
                      {jobs.map((job) => {
                        // 1. QUEUED STATUS CARD
                        if (job.status === "queued") {
                          return (
                            <div
                              key={job.id}
                              className={`ai-gen-card border-amber-200/80 bg-white ${
                                entityType === "model"
                                  ? "w-[185px] sm:w-[200px]"
                                  : "w-[230px] sm:w-[250px]"
                              } shrink-0 shadow-2xs`}
                            >
                              <div
                                className={`w-full ${cardAspect} bg-amber-50/50 relative flex flex-col items-center justify-center p-3 text-center border-b border-amber-100/60`}
                              >
                                <div className="w-9 h-9 rounded-full bg-amber-100/90 text-amber-700 flex items-center justify-center mb-2 shadow-2xs">
                                  <MdHourglassEmpty className="animate-pulse text-lg" />
                                </div>
                                <span className="text-[11px] font-bold text-amber-900 px-2.5 py-0.5 rounded bg-white/90 shadow-2xs border border-amber-200/70 max-w-[90%] truncate">
                                  {job.stageText || "Waiting in server queue..."}
                                </span>

                                <div className="absolute top-1.5 left-1.5">
                                  <span className="text-[9px] font-extrabold uppercase px-1.5 py-0.5 rounded bg-amber-200/80 text-amber-900 border border-amber-300/80 tracking-wide">
                                    In Queue
                                  </span>
                                </div>

                                <button
                                  type="button"
                                  onClick={() => onCancel(job.id)}
                                  className="absolute top-1.5 right-1.5 w-5 h-5 rounded-full bg-white/90 hover:bg-white text-slate-500 hover:text-slate-800 flex items-center justify-center cursor-pointer border border-slate-200 shadow-2xs"
                                  title="Cancel queued job"
                                >
                                  <MdClose size={13} />
                                </button>
                              </div>

                              <div className="p-2.5 bg-white border-t border-slate-100 flex flex-col gap-1">
                                <span className="text-[10px] uppercase font-bold text-amber-700">
                                  {job.gender}
                                </span>
                                <ExpandablePrompt name={job.name} prompt={job.prompt} />
                              </div>
                            </div>
                          );
                        }

                        // 2. GENERATING STATUS CARD
                        if (job.status === "generating") {
                          return (
                            <div
                              key={job.id}
                              className={`ai-gen-card border-slate-200 bg-white ${
                                entityType === "model"
                                  ? "w-[185px] sm:w-[200px]"
                                  : "w-[230px] sm:w-[250px]"
                              } shrink-0 shadow-2xs`}
                            >
                              <div
                                className={`w-full ${cardAspect} ai-gen-shimmer-bg relative flex flex-col items-center justify-center p-3 text-center`}
                              >
                                <div className="w-9 h-9 rounded-full bg-white/90 backdrop-blur shadow-sm flex items-center justify-center text-primary mb-2">
                                  <MdAutoAwesome className="animate-spin text-lg" />
                                </div>
                                <span className="text-[11px] font-bold text-slate-900 px-2 py-0.5 rounded bg-white/80 backdrop-blur max-w-[90%] truncate shadow-2xs">
                                  {job.stageText}
                                </span>

                                <div className="absolute top-1.5 left-1.5">
                                  <span className="text-[9px] font-extrabold uppercase px-1.5 py-0.5 rounded bg-primary/10 text-primary border border-primary/20 tracking-wide">
                                    Synthesizing
                                  </span>
                                </div>

                                {/* Bottom Sleek Indeterminate Shimmer Line */}
                                <div className="absolute bottom-2.5 left-3 right-3">
                                  <div className="w-full h-1 bg-white/70 rounded-full overflow-hidden relative shadow-2xs">
                                    <div className="ai-gen-indeterminate-bar h-full bg-primary" />
                                  </div>
                                </div>

                              </div>

                              <div className="p-2.5 bg-white border-t border-slate-100 flex flex-col gap-1">
                                <span className="text-[10px] uppercase font-bold text-primary">
                                  {job.gender}
                                </span>
                                <ExpandablePrompt name={job.name} prompt={job.prompt} />
                              </div>
                            </div>
                          );
                        }

                        // 3. FAILED STATUS CARD
                        if (job.status === "failed") {
                          return (
                            <div
                              key={job.id}
                              className={`ai-gen-card border-red-200 bg-white ${
                                entityType === "model"
                                  ? "w-[185px] sm:w-[200px]"
                                  : "w-[230px] sm:w-[250px]"
                              } shrink-0 shadow-2xs`}
                            >
                              <div
                                className={`w-full ${cardAspect} bg-red-50/60 relative flex flex-col items-center justify-center p-3 text-center`}
                              >
                                {/* Status / Gender tag overlay */}
                                <div className="absolute top-1.5 left-1.5 flex items-center gap-1 z-10">
                                  {job.gender && (
                                    <span className="text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-black/60 backdrop-blur-xs text-white leading-none">
                                      {job.gender}
                                    </span>
                                  )}
                                  <span className="text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-red-600/90 text-white leading-none">
                                    Failed
                                  </span>
                                </div>

                                <div className="w-9 h-9 rounded-full bg-red-100 text-red-600 flex items-center justify-center mb-1 shadow-2xs">
                                  <MdErrorOutline size={20} />
                                </div>
                                <span className="text-xs font-bold text-red-700 mb-0.5">
                                  Generation Failed
                                </span>
                                <span className="text-[10px] text-red-600 max-w-[90%] line-clamp-2 leading-tight">
                                  {job.error || "Generation timed out"}
                                </span>

                                <button
                                  type="button"
                                  onClick={() => {
                                    if (onDeleteHistoryItem) {
                                      onDeleteHistoryItem(job.id);
                                    } else if (onDismiss) {
                                      onDismiss(job.id);
                                    }
                                  }}
                                  className="absolute top-1.5 right-1.5 w-6 h-6 rounded-full bg-white/90 hover:bg-red-50 text-slate-400 hover:text-red-600 flex items-center justify-center cursor-pointer border border-slate-200/60 hover:border-red-200 shadow-2xs transition-all"
                                  title="Delete failed generation"
                                  aria-label="Delete failed generation"
                                >
                                  <MdDeleteOutline size={15} />
                                </button>
                              </div>

                              <div className="p-2 bg-white border-t border-red-100 flex items-center justify-between gap-1.5">
                                <div className="flex-1 min-w-0">
                                  <ExpandablePrompt name={job.name} prompt={job.prompt} />
                                </div>
                                <button
                                  type="button"
                                  onClick={() => {
                                    if (onDeleteHistoryItem) {
                                      onDeleteHistoryItem(job.id);
                                    } else if (onDismiss) {
                                      onDismiss(job.id);
                                    }
                                  }}
                                  className="px-2 py-1 bg-red-50 hover:bg-red-100 text-red-600 border border-red-200 text-[11px] font-bold rounded flex items-center gap-1 cursor-pointer transition-colors shrink-0 shadow-2xs"
                                  title="Delete failed generation"
                                >
                                  <MdDeleteOutline size={13} />
                                  <span>Delete</span>
                                </button>
                              </div>
                            </div>
                          );
                        }

                        return null;
                      })}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* SECTION 2: GENERATED DRAFTS & HISTORY */}
          <div className="!mt-0 !pt-0">
            <div className="flex items-center justify-between mb-3 pb-2 border-b border-slate-200/80">
              <div className="flex items-center gap-2">
                <h3 className="text-xs sm:text-sm font-bold text-slate-800 tracking-tight m-0">
                  Generated {pluralLabel}
                </h3>
                <span className="text-[11px] font-semibold text-slate-500 bg-slate-100 px-2 py-0.5 rounded-full border border-slate-200/60">
                  {activeTotal} {activeTotal === 1 ? "draft" : "drafts"}
                </span>
                {isFetching && (
                  <span className="inline-flex items-center gap-1 text-[11px] text-slate-400 font-medium">
                    <MdRefresh className="animate-spin text-primary" size={13} />
                    <span>Syncing...</span>
                  </span>
                )}
              </div>
            </div>

            {paginatedHistory.length === 0 ? (
              <div className="py-16 bg-white border border-slate-200/80 rounded-2xl flex flex-col items-center justify-center text-center p-8">
                <div className="w-12 h-12 rounded-full bg-slate-100 text-primary border border-slate-200/80 flex items-center justify-center mb-3">
                  <MdAutoAwesome size={24} />
                </div>
                <h4 className="text-sm font-bold text-slate-800 mb-1">
                  {searchTerm || statusFilter !== "all"
                    ? "No drafts matching your filter"
                    : `No AI generated ${entityType === "model" ? "models" : "backgrounds"} yet`}
                </h4>
                <p className="text-xs text-slate-500 max-w-sm mb-4">
                  {searchTerm || statusFilter !== "all"
                    ? "Try resetting your search query or status filter."
                    : entityType === "model"
                    ? "Use the AI model studio to configure attributes and synthesize custom fashion models."
                    : "Use the prompt studio to synthesize custom luxury backdrops and add them to your catalog."}
                </p>
                <button
                  type="button"
                  onClick={onOpenDrawer}
                  className="app-button-primary flex items-center justify-center gap-1.5 px-4 py-2 rounded-lg text-xs font-bold shadow-sm cursor-pointer whitespace-nowrap"
                >
                  <MdAutoAwesome size={15} />
                  <span>{createButtonText}</span>
                </button>
              </div>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 2xl:grid-cols-5 gap-x-3 sm:gap-x-5 gap-y-4 sm:gap-y-6 items-start">
                {paginatedHistory.map((item) => (
                  <div
                    key={item.id}
                    className="ai-gen-card border-slate-200 bg-white hover:border-slate-300"
                  >
                    {/* Image / Thumbnail */}
                    <div
                      className={`w-full ${cardAspect} bg-slate-100 relative overflow-hidden group`}
                    >
                      {item.resultUrl ? (
                        <img
                          src={item.resultUrl}
                          alt={item.prompt || item.name}
                          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                          loading="lazy"
                        />
                      ) : (
                        <div
                          className={`w-full h-full flex flex-col items-center justify-center p-3 text-center ${
                            item.status === "failed" ? "bg-red-50/70" : "bg-slate-100"
                          }`}
                        >
                          <MdErrorOutline
                            size={22}
                            className={item.status === "failed" ? "text-red-500 mb-1 shrink-0" : "text-slate-400 mb-1 shrink-0"}
                          />
                          {item.status === "failed" ? (
                            <div className="px-2 max-w-full">
                              <span className="text-[11px] font-bold text-red-700 block mb-0.5 leading-tight">
                                Failed render
                              </span>
                              <p
                                className="text-[10px] text-red-600 font-medium line-clamp-3 leading-snug m-0 break-words"
                                title={item.error || "Generation failed"}
                              >
                                {item.error || "Generation failed. Please refine your prompt."}
                              </p>
                            </div>
                          ) : (
                            <span className="text-[10px] text-slate-500 font-medium">
                              No preview available
                            </span>
                          )}
                        </div>
                      )}

                      {/* Status / Gender tag overlay: Neatly aligned in a row */}
                      <div className="absolute top-1.5 left-1.5 flex items-center gap-1 z-10">
                        {item.gender && (
                          <span className="text-[9px] font-medium uppercase tracking-tight px-1.5 py-1 rounded bg-black/60 backdrop-blur-xs text-white leading-none">
                            {item.gender}
                          </span>
                        )}
                        {item.status === "failed" && (
                          <span className="text-[9px] font-medium uppercase tracking-tight px-1.5 py-1 rounded bg-red-600/90 text-white leading-none">
                            Failed
                          </span>
                        )}
                      </div>

                      {/* Top-Right Delete Button (styled like ZoomButton with red strokes) */}
                      {onDeleteHistoryItem && (
                        <button
                          type="button"
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            if (item.status === "generating") {
                              toast.error("Active generation cannot be deleted while in progress.");
                              return;
                            }
                            onDeleteHistoryItem(item.id);
                          }}
                          disabled={item.status === "generating"}
                          className="absolute top-1.5 right-1.5 z-10 w-5 h-5 max-[640px]:w-4 max-[640px]:h-4 rounded-full bg-white text-red-500 hover:text-red-600 hover:bg-red-50/70 flex items-center justify-center shadow-[0_2px_8px_rgba(0,0,0,0.08)] hover:scale-105 active:scale-95 transition-all border border-red-300 hover:border-red-400 cursor-pointer focus:outline-none"
                          title={
                            item.status === "generating"
                              ? "Generating jobs cannot be deleted"
                              : "Delete draft"
                          }
                          aria-label="Delete draft"
                        >
                          <MdDeleteOutline
                            className="shrink-0 max-[640px]:!w-2.5 max-[640px]:!h-2.5"
                            size={12}
                          />
                        </button>
                      )}

                      {/* Zoom Button */}
                      {onZoom && item.resultUrl && (
                        <ZoomButton
                          modalTarget={modalTarget}
                          onClick={(e) => {
                            if (e?.stopPropagation) e.stopPropagation();
                            onZoom(item.resultUrl);
                          }}
                          positionClasses="absolute bottom-1.5 right-1.5 z-10"
                          size="md"
                        />
                      )}
                    </div>

                    {/* Compact Card Footer: Title & Date on Left, Action Pill on Right */}
                    <div className="p-2 sm:p-2.5 bg-white border-t border-slate-100 flex items-center justify-between gap-1.5 min-w-0">
                      <div className="min-w-0 flex-1">
                        <ExpandablePrompt name={item.name} prompt={item.prompt} />

                        {/* Proper Date Display */}
                        <div className="flex items-center gap-1 text-[9px] sm:text-[9.5px] font-medium text-slate-400 mt-0.5 leading-none whitespace-nowrap overflow-hidden">
                          <MdCalendarToday size={10} className="text-slate-400 shrink-0" />
                          <span className="truncate">{formatProperDate(item.createdAt)}</span>
                        </div>
                      </div>

                      {/* Right-Aligned Action Button / Status */}
                      <div className="shrink-0 flex items-center">
                        {item.status === "failed" ? (
                          <span className="inline-flex items-center gap-1.5 h-7 px-2.5 rounded-lg text-[10.5px] font-semibold text-red-600 bg-red-50 border border-red-200/80 leading-none select-none shrink-0">
                            <span className="w-1.5 h-1.5 rounded-full bg-red-500 shrink-0" />
                            <span>Failed</span>
                          </span>
                        ) : item.savedToLibrary ? (
                          <span
                            className="inline-flex items-center gap-1.5 h-7 px-2.5 rounded-lg text-[11px] font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200/80 shadow-2xs leading-none select-none shrink-0"
                            title="Added to your Library"
                          >
                            <MdCheck size={13} className="text-emerald-600 shrink-0" />
                            <span>Added</span>
                          </span>
                        ) : (
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              onSaveToLibrary(item);
                            }}
                            className="inline-flex items-center gap-1.5 h-6 px-3 rounded-md text-[11px] font-semibold bg-slate-900 hover:bg-black text-white border border-slate-900 shadow-2xs hover:shadow-xs active:scale-95 transition-all cursor-pointer whitespace-nowrap leading-none shrink-0"
                            title="Add to Library"
                          >
                            <MdAdd size={14} className="shrink-0 text-white/90" />
                            <span>Add</span>
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Always Visible Footer Bar matching Library pagination */}
      {activeTotal > 0 && (
        <footer className="app-footer-bar shrink-0 border-t border-slate-200/90 bg-white/95 backdrop-blur-sm px-3 sm:px-6 py-1.5 z-20 shadow-[0_-2px_8px_rgba(26,32,48,0.03)] flex items-center min-h-[38px] overflow-x-auto">
          <InternalPagination
            page={activePage}
            limit={activeLimit}
            total={activeTotal}
            totalPages={activeTotalPages}
            limitOptions={[10, 20, 30, 50]}
            onPageChange={handlePageChange}
            onLimitChange={handleLimitChange}
            showFirstLast={true}
            rowsLabel="Items per page:"
          />
        </footer>
      )}
    </div>
  );
};

export default AIGenerationsView;
